import React from 'react';
import { X, GitCompare, Trash2 } from 'lucide-react';
import { useCompare } from '@/contexts/CompareContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const formatFollowers = (count: number | null | undefined): string => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

export const CompareTray: React.FC = () => {
  const {
    selectedInfluencers,
    removeFromCompare,
    clearCompare,
    setIsCompareOpen,
    MIN_TO_COMPARE,
    MAX_TO_COMPARE,
  } = useCompare();

  const isMobile = useIsMobile();
  const count = selectedInfluencers.length;
  const canCompare = count >= MIN_TO_COMPARE;

  // Don't render if no influencers selected
  if (count === 0) return null;

  const handleCompare = () => {
    if (canCompare) {
      setIsCompareOpen(true);
    }
  };

  // Mobile: Floating button
  if (isMobile) {
    return (
      <div className="fixed bottom-4 right-4 z-50 animate-scale-in">
        <Button
          onClick={handleCompare}
          disabled={!canCompare}
          className="h-14 w-14 rounded-full shadow-lg relative"
          size="icon"
          type="button"
        >
          <GitCompare className="h-6 w-6" />
          <Badge 
            className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            variant="secondary"
          >
            {count}
          </Badge>
        </Button>
      </div>
    );
  }

  // Desktop: Full tray
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-fade-in select-none">
      <div className="mx-auto max-w-5xl px-4 pb-4">
        <div className="bg-card border border-border rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            {/* Selected influencers */}
            <div className="flex items-center gap-3 flex-1 overflow-x-auto">
              {selectedInfluencers.map((influencer) => (
                <div
                  key={influencer.id}
                  className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 min-w-fit group animate-scale-in"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage 
                      src={influencer.profile_pic_path || undefined} 
                      alt={influencer.name} 
                    />
                    <AvatarFallback className="text-xs">
                      {influencer.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate max-w-24">
                      {influencer.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatFollowers(influencer.total_followers_count)} followers
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFromCompare(influencer.id)}
                    className="ml-1 p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label={`Remove ${influencer.name} from comparison`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* Empty slots indicator */}
              {Array.from({ length: MAX_TO_COMPARE - count }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="flex items-center justify-center h-12 w-12 rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground/50"
                >
                  <span className="text-xs">+</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 border-l border-border pl-4">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {count} of {MAX_TO_COMPARE}
              </span>
              
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={clearCompare}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>

              <Button
                type="button"
                onClick={handleCompare}
                disabled={!canCompare}
                className={cn(
                  "min-w-[120px]",
                  !canCompare && "opacity-50"
                )}
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Compare ({count})
              </Button>
            </div>
          </div>

          {/* Helper text when less than minimum */}
          {!canCompare && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Select at least {MIN_TO_COMPARE} influencers to compare
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
